import { Box, Input, NativeSelect, Table, Text } from '@chakra-ui/react'
import { useSortable } from '@dnd-kit/react/sortable'
import { GripVertical } from 'lucide-react'
import React from 'react'

import type { DevicesTranslationValues } from '../i18n'
import {
  type ButtonFunctionOption,
  type DraftButton,
  getButtonFunctionOption,
  LEGACY_PARKING_LOTS,
} from './buttonConstants'

export interface SortableButtonRowProps {
  draft: DraftButton
  idx: number
  type: 'LineButton' | 'SidecarButton'
  isFirstLineRow: boolean
  isSortingDisabled: boolean
  functionOptions: ButtonFunctionOption[]
  duplicatePresenceExtIds: Set<number>
  duplicateLineExtIds: Set<number>
  missingExtension?: boolean
  lineExtensions: Array<{ id: number; number: string; name: string }>
  presenceExtensions: Array<{ id: number; number: string; name: string }>
  voicemailExtensions: Array<{ id: number; number: string; name: string }>
  text: (
    key: string,
    fallback: string,
    vars?: DevicesTranslationValues,
  ) => string
  updateDraft: (
    type: 'LineButton' | 'SidecarButton',
    index: number,
    field: keyof DraftButton,
    value: string | number,
  ) => void
}

export const SortableButtonRow: React.FC<SortableButtonRowProps> = React.memo(
  ({
    draft: d,
    idx,
    type,
    isFirstLineRow,
    isSortingDisabled,
    functionOptions,
    duplicatePresenceExtIds,
    duplicateLineExtIds,
    missingExtension = false,
    lineExtensions,
    presenceExtensions,
    voicemailExtensions,
    text,
    updateDraft,
  }) => {
    const selectedOption = getButtonFunctionOption(d.button_function)
    const fnType = selectedOption?.type
    const isPresetSpeedDial =
      fnType === 'speed_dial' && selectedOption?.value !== 'speed_dial'

    const { ref, handleRef, isDragSource, sortable } = useSortable({
      id: d._sortId,
      index: idx,
      type,
      group: type,
      disabled: isSortingDisabled,
      transition: { duration: 200, easing: 'ease' },
    })

    return (
      <Table.Row ref={ref} opacity={isDragSource ? 0.5 : 1}>
        <Table.Cell px={1} py={2} w="36px">
          {!isSortingDisabled ? (
            <Box
              ref={handleRef}
              cursor="grab"
              color="fg.muted"
              display="flex"
              alignItems="center"
              justifyContent="center"
              _hover={{ color: 'fg' }}
            >
              <GripVertical size={16} />
            </Box>
          ) : (
            <Box w="16px" />
          )}
        </Table.Cell>
        <Table.Cell px={3} py={2} w="50px">
          {sortable.index + 1}
        </Table.Cell>

        <Table.Cell px={3} py={2}>
          <NativeSelect.Root size="sm" disabled={isFirstLineRow}>
            <NativeSelect.Field
              value={d.button_function}
              onChange={(e) =>
                updateDraft(type, idx, 'button_function', e.target.value)
              }
            >
              {isFirstLineRow ? (
                <option value="line">
                  {text('devices.buttons.functionLine', 'Line')}
                </option>
              ) : (
                functionOptions.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </option>
                ))
              )}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Table.Cell>

        <Table.Cell px={3} py={2}>
          {fnType === 'line' || fnType === 'presence' ? (
            <NativeSelect.Root
              size="sm"
              disabled={isFirstLineRow}
              {...((fnType === 'presence' &&
                d.extension_id !== '' &&
                duplicatePresenceExtIds.has(Number(d.extension_id))) ||
              (fnType === 'line' &&
                !isFirstLineRow &&
                d.extension_id !== '' &&
                duplicateLineExtIds.has(Number(d.extension_id))) ||
              missingExtension
                ? {
                    css: {
                      '& select': {
                        borderColor: 'red.500',
                        boxShadow: '0 0 0 1px var(--chakra-colors-red-500)',
                      },
                    },
                  }
                : {})}
            >
              <NativeSelect.Field
                value={d.extension_id === '' ? '' : d.extension_id}
                onChange={(e) =>
                  updateDraft(
                    type,
                    idx,
                    'extension_id',
                    e.target.value === '' ? '' : Number(e.target.value),
                  )
                }
              >
                {fnType !== 'presence' && (
                  <option value="">
                    {text('devices.buttons.extensionNone', '-- None --')}
                  </option>
                )}
                {(fnType === 'presence'
                  ? presenceExtensions
                  : lineExtensions
                ).map((ext) => (
                  <option key={ext.id} value={ext.id}>
                    {ext.number} - {ext.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          ) : fnType === 'voicemail' ? (
            <NativeSelect.Root
              size="sm"
              {...(missingExtension
                ? {
                    css: {
                      '& select': {
                        borderColor: 'red.500',
                        boxShadow: '0 0 0 1px var(--chakra-colors-red-500)',
                      },
                    },
                  }
                : {})}
            >
              <NativeSelect.Field
                value={d.extension_id === '' ? '' : d.extension_id}
                onChange={(e) =>
                  updateDraft(
                    type,
                    idx,
                    'extension_id',
                    e.target.value === '' ? '' : Number(e.target.value),
                  )
                }
              >
                <option value="">
                  {text('devices.buttons.extensionNone', '-- None --')}
                </option>
                {voicemailExtensions.map((ext) => (
                  <option key={ext.id} value={ext.id}>
                    {ext.number} - {ext.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          ) : fnType === 'parking_slot' ? (
            <NativeSelect.Root size="sm">
              <NativeSelect.Field
                value={d.button_value}
                onChange={(e) =>
                  updateDraft(type, idx, 'button_value', e.target.value)
                }
              >
                {LEGACY_PARKING_LOTS.map((lot) => (
                  <option key={lot} value={String(lot)}>
                    {text(
                      'devices.buttons.parkingLotOption',
                      'Parking Lot {{number}}',
                      {
                        number: lot < 59100 ? lot - 5900 : lot - 59000,
                      },
                    )}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          ) : fnType === 'speed_dial' ? (
            <Input
              size="sm"
              value={d.speed_dial_value}
              onChange={(e) =>
                updateDraft(type, idx, 'speed_dial_value', e.target.value)
              }
              placeholder={text(
                'devices.buttons.phoneNumberPlaceholder',
                'Phone number',
              )}
              disabled={isPresetSpeedDial}
            />
          ) : (
            <Text color="fg.muted" fontSize="sm">
              --
            </Text>
          )}
        </Table.Cell>

        <Table.Cell px={3} py={2}>
          {fnType && fnType !== 'rollover' ? (
            <Input
              size="sm"
              value={d.button_label}
              onChange={(e) =>
                updateDraft(type, idx, 'button_label', e.target.value)
              }
              placeholder={text(
                'devices.buttons.optionalLabelPlaceholder',
                'Optional label',
              )}
            />
          ) : (
            <Text color="fg.muted" fontSize="sm">
              --
            </Text>
          )}
        </Table.Cell>
      </Table.Row>
    )
  },
)
